import { Search } from 'lucide-react';
import { Suspense, lazy, useCallback, useEffect, useState, type ReactNode } from 'react';
import type { Route } from '../app/router';

/**
 * Der Einstieg in die Power-Suche: das Feld in der Kopfzeile und `⌘K`.
 *
 * **Diese Datei ist absichtlich klein**, denn sie liegt im **Hauptbündel**: Die
 * Kopfzeile gehört zum ersten sichtbaren Bild und wird nicht nachgeladen
 * (`App.tsx`). Alles, was die Suche wirklich tut — Abfrage, Gruppen,
 * Hervorhebung, Tastaturführung — steht in `SucheOverlay.tsx` und kommt beim
 * ersten Öffnen über `React.lazy` dazu. Wer das umdreht, legt eine Maske, die
 * die meisten Bildaufbauten nie zu sehen bekommen, in das Bündel, das jeder
 * Bildaufbau lädt.
 *
 * **Der Knopf sieht aus wie ein Feld und ist keines.** Ein echtes `<input>`
 * hier hiesse: zwei Eingabefelder für dieselbe Eingabe, und der Anwender
 * tippte in das eine, während das andere den Fokus bekommt. Der Knopf öffnet
 * die Überlagerung, und dort steht das eine Feld, in dem getippt wird.
 *
 * **`⌘K` / `Strg+K` wirkt global — ausser der Fokus steht in einem anderen
 * Textfeld.** Dort tippt gerade jemand etwas anderes, und eine Suche, die sich
 * mitten in eine Belegerfassung schiebt, ist ein Datenverlust mit
 * Tastenkürzel.
 */

const SucheOverlay = lazy(() =>
  import('./SucheOverlay').then((m) => ({ default: m.SucheOverlay })),
);

/** Steht der Fokus gerade in einem Feld, in das jemand schreibt? */
function tipptGerade(ziel: EventTarget | null): boolean {
  if (!(ziel instanceof HTMLElement)) return false;
  if (ziel.isContentEditable) return true;
  const name = ziel.tagName;
  return name === 'INPUT' || name === 'TEXTAREA' || name === 'SELECT';
}

export function Suchfeld(props: { readonly navigate: (route: Route) => void }): ReactNode {
  const [offen, setOffen] = useState(false);
  const schliessen = useCallback(() => {
    setOffen(false);
  }, []);

  useEffect(() => {
    const beiTaste = (event: KeyboardEvent): void => {
      if (event.key !== 'k' && event.key !== 'K') return;
      if (!event.metaKey && !event.ctrlKey) return;
      if (tipptGerade(event.target)) return;
      event.preventDefault();
      setOffen(true);
    };
    window.addEventListener('keydown', beiTaste);
    return () => {
      window.removeEventListener('keydown', beiTaste);
    };
  }, []);

  return (
    <>
      {/*
        270 px — der Platz, den das Übergabepaket der Design-Annäherung dafür
        vorsieht (Spec 9.1: [Hamburger] [Brotkrumen] ——— [Suchfeld] [Farbschema]
        [Neu]). Unterhalb von `lg` fällt er weg: Dort ist die Kopfzeile schon
        ohne ihn zweizeilig, und `⌘K` gibt es auf einem Telefon nicht.
      */}
      <button
        type="button"
        aria-label="Suche öffnen"
        onClick={() => {
          setOffen(true);
        }}
        className="hidden h-[34px] w-[270px] shrink-0 items-center gap-2 rounded-md border border-border bg-background px-3 text-left text-[13px] text-text3 outline-none transition-colors hover:bg-secondary focus-visible:ring-2 focus-visible:ring-ring lg:flex"
      >
        <Search aria-hidden="true" strokeWidth={1.7} className="size-4 shrink-0" />
        <span className="min-w-0 flex-1 truncate">Suchen</span>
        {/*
          Der Hinweis steht IM Feld und nicht daneben. Er ist die einzige Stelle,
          an der ein Anwender das Tastenkürzel erfährt, ohne danach zu suchen.
        */}
        <kbd className="shrink-0 rounded border border-border px-1 text-[11px] text-text3">⌘K</kbd>
      </button>

      {offen ? (
        // Kein Ladehinweis: Das Bündel ist klein, und ein Blinken zwischen
        // Tastendruck und Feld wäre auffälliger als die Wartezeit selbst.
        <Suspense fallback={null}>
          <SucheOverlay onClose={schliessen} navigate={props.navigate} />
        </Suspense>
      ) : null}
    </>
  );
}
